// usePeriodState — shared period (start/end) state hook for MEC pages.
//
// Read order: URL search params (?start=&end=) → localStorage → defaultDashboardRange().
//
// • URL is the source of truth on every render — derive periodStart / periodEnd
//   from searchParams.get() so browser back/forward navigation flips state
//   correctly without re-mounting the calling component.
// • On first mount, if the URL is empty, sync the chosen initial values into
//   the URL (history.replaceState — no extra entry) and into localStorage so
//   the next visit starts where the user left off.
// • setPeriod(start, end) writes both URL and localStorage atomically.
//
// `key` separates storage namespaces between pages (Dashboard '_global',
// Locations '_locations', etc.) so flipping the dashboard's period doesn't
// quietly change the Locations page on next visit.

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { defaultDashboardRange } from './PeriodPicker';

const STORAGE_PREFIX = 'mec.lastPeriod.';

export function usePeriodState(key = '_global') {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read order: URL → localStorage → default
  const urlStart = searchParams.get('start');
  const urlEnd = searchParams.get('end');

  let initialStart;
  let initialEnd;
  if (urlStart && urlEnd) {
    initialStart = urlStart;
    initialEnd = urlEnd;
  } else {
    try {
      const stored = localStorage.getItem(STORAGE_PREFIX + key);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && parsed.start && parsed.end) {
          initialStart = parsed.start;
          initialEnd = parsed.end;
        }
      }
    } catch (_) {
      // localStorage unavailable (private mode / blocked) — fall through.
    }
    if (!initialStart) {
      const def = defaultDashboardRange();
      initialStart = def.start;
      initialEnd = def.end;
    }
  }

  // Sync URL on first mount when it's empty so deep-links + refresh work.
  useEffect(() => {
    if (!urlStart || !urlEnd) {
      const next = new URLSearchParams(searchParams);
      next.set('start', initialStart);
      next.set('end', initialEnd);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-derive from searchParams on every render so back/forward + external
  // URL edits flow into the consuming component.
  const periodStart = searchParams.get('start') || initialStart;
  const periodEnd = searchParams.get('end') || initialEnd;

  const setPeriod = (start, end) => {
    const next = new URLSearchParams(searchParams);
    next.set('start', start);
    next.set('end', end);
    setSearchParams(next, { replace: true });
    try {
      localStorage.setItem(
        STORAGE_PREFIX + key,
        JSON.stringify({ start, end }),
      );
    } catch (_) {
      // Ignore — best-effort persistence only.
    }
  };

  return { periodStart, periodEnd, setPeriod };
}

export default usePeriodState;
